import { type IGobernanzaAprDetail } from "@modules/gobernanza/interfaces/IGobernanzaAprDetailResponse";
import { GobernanzaAprService } from "@modules/gobernanza/services/gobernanza-apr.service";
import { finalize } from "rxjs";

import { inject, Service, signal } from "@angular/core";

@Service()
export class GobernanzaAprDetailCacheService {
  private readonly aprService = inject(GobernanzaAprService);

  cache = signal<Map<number, IGobernanzaAprDetail>>(new Map());
  loadingId = signal<number | null>(null);
  errorId = signal<number | null>(null);
  notFoundId = signal<number | null>(null);

  get(id: number) {
    return this.cache().get(id) ?? null;
  }

  ensureLoaded(id: number) {
    if (this.cache().has(id)) return;
    if (this.loadingId() === id) return;
    if (this.notFoundId() === id) return;
    this.fetch(id);
  }

  retry(id: number) {
    this.fetch(id);
  }

  upsert(id: number, detail: IGobernanzaAprDetail) {
    this.cache.update(cache => {
      const next = new Map(cache);
      next.set(id, detail);
      return next;
    });
    this.notFoundId.update(current => (current === id ? null : current));
    this.errorId.update(current => (current === id ? null : current));
  }

  remove(id: number) {
    this.cache.update(cache => {
      if (!cache.has(id)) return cache;
      const next = new Map(cache);
      next.delete(id);
      return next;
    });
  }

  clear() {
    this.cache.set(new Map());
    this.loadingId.set(null);
    this.errorId.set(null);
    this.notFoundId.set(null);
  }

  private fetch(id: number) {
    this.loadingId.set(id);
    this.errorId.set(null);
    this.notFoundId.set(null);

    this.aprService
      .getAprById(id)
      .pipe(
        finalize(() => {
          if (this.loadingId() === id) this.loadingId.set(null);
        })
      )
      .subscribe({
        next: response => {
          this.cache.update(cache => {
            const next = new Map(cache);
            next.set(id, response.data);
            return next;
          });
        },
        error: error => {
          if (error.status === 404) {
            this.notFoundId.set(id);
            return;
          }
          this.errorId.set(id);
        },
      });
  }
}
